import Dialog from '@material-ui/core/Dialog'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { getExportPriceRanges } from 'redux/actions/data'

import LoadingIndicator from 'components/atoms/loading-indicator'
import { PriceEvaluationBlocks } from 'components/molecules/price-evaluation-indicator'
import PriceEvaluationScale from 'components/molecules/price-evaluation-scale'
import {
  getPriceEvaluationRanges,
  normalizePriceEvaluation,
  priceEvaluationColors,
  priceEvaluationSteps,
} from 'utilities/price-evaluation'

export const EUROSTOCKS_URL = 'https://www.eurostocks.com/nl/contact'
export const MOBILE_DE_URL = 'https://www.mobile.de'

const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.sizeByFactor(1.25)};
    width: ${({ theme }) => theme.sizeByFactor(75)};
    padding: ${({ theme }) => theme.sizings.lvl3};
    background: ${({ theme }) => theme.colors.isolatedBackground};
    box-shadow: ${({ theme }) => theme.shadows.modal};
    font-family: ${({ theme }) => theme.font};
    color: ${({ theme }) => theme.colors.text};
  }

  .MuiBackdrop-root {
    background: ${({ theme }) =>
      theme.colors.dialogBackdrop || `${theme.colors.brandAlfa}cc`};
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.sizeByFactor(2.5)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.sizings.lvl3};
`

const SectionTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.level2Heading};
  font-size: ${({ theme }) => theme.sizeByFactor(2)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.sizings.lvl3};
`

const Paragraph = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.smallFontSize};
  font-weight: 400;
  line-height: ${({ theme }) => theme.sizings.lvl2};
`

const ExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.actionsStandard};
  text-decoration: none;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(1.25)};
`

const ContactText = styled(Paragraph)`
  flex: 1 1 auto;
`

const Logo = styled.img`
  flex-shrink: 0;
  width: ${({ theme }) => theme.sizeByFactor(15.5)};
  height: auto;
`

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
  padding: ${({ theme }) => theme.sizings.lvl2};
  background-color: ${({ theme }) => theme.colors.sectionBackground};
`

const LegendItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizeByFactor(0.75)};
`

const LegendHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizeByFactor(0.5)};
`

const LegendName = styled.span`
  color: ${({ theme, $color }) => theme.colors[$color]};
  font-size: 11px;
  font-weight: 600;
  line-height: ${({ theme }) => theme.smallFontSize};
`

const CloseButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.colors.actionsStandard};
  font-family: ${({ theme }) => theme.font};
  font-size: ${({ theme }) => theme.smallFontSize};
  font-weight: 400;
  line-height: ${({ theme }) => theme.sizings.lvl2};
  cursor: pointer;
`

const legendCategories = [...priceEvaluationSteps, 'none']

/**
 * Explains the mobile.de price evaluation of a car: where its price sits on
 * the scale of comparable cars and what every category means. Opened from the
 * info icon next to the evaluation blocks on the stock cards and the car file.
 */
const PriceEvaluationDialog = ({
  open,
  onClose,
  carId,
  evaluation,
  className,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const exportPriceRanges = useSelector((state) => state.data.exportPriceRanges)
  const loading = !!exportPriceRanges?.loading
  const details = getPriceEvaluationRanges(exportPriceRanges?.data)
  // Whether mobile.de could evaluate this car is known from the car itself; the
  // ranges are only needed to draw the scale.
  const step = normalizePriceEvaluation(evaluation) || details?.evaluation
  const hasEvaluation = !!step

  useEffect(() => {
    if (open && carId) {
      dispatch(getExportPriceRanges(carId))
    }
  }, [dispatch, open, carId])

  const mobileDeLink = (
    <ExternalLink
      key="trans-component-link"
      href={MOBILE_DE_URL}
      target="_blank"
      rel="noopener noreferrer"
    />
  )

  const introduction = (
    <>
      <Paragraph>{t('priceEvaluationDialog.intro')}</Paragraph>
      <ContactRow>
        <ContactText>
          {t('priceEvaluationDialog.contactQuestion')}
          <br />
          <Trans
            i18nKey="priceEvaluationDialog.contactLink"
            components={[
              <ExternalLink
                key="trans-component-link"
                href={EUROSTOCKS_URL}
                target="_blank"
                rel="noopener noreferrer"
              />,
            ]}
          />
        </ContactText>
        <Logo src="/images/logo-eurostocks.svg" alt="EuroStocks" />
      </ContactRow>
    </>
  )

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      className={className}
      maxWidth={false}
      aria-labelledby="price-evaluation-dialog-title"
      PaperProps={{ square: true, 'data-test-e2e': 'price-evaluation-dialog' }}
    >
      <Title id="price-evaluation-dialog-title">
        {t('priceEvaluationDialog.title')}
      </Title>
      {!hasEvaluation && (
        <>
          <SectionTitle>
            {t('priceEvaluationDialog.unavailableTitle')}
          </SectionTitle>
          <Paragraph>
            {t('priceEvaluationDialog.unavailableDescription')}
          </Paragraph>
        </>
      )}
      {introduction}
      <SectionTitle>{t('priceEvaluationDialog.methodologyTitle')}</SectionTitle>
      <Paragraph>
        <Trans
          i18nKey="priceEvaluationDialog.methodology"
          components={[mobileDeLink]}
        />
      </Paragraph>
      {hasEvaluation && loading && <LoadingIndicator size="small" />}
      {hasEvaluation && !loading && !!details && (
        <PriceEvaluationScale
          price={details.price}
          evaluation={step}
          ranges={details.ranges}
        />
      )}
      <Legend>
        {legendCategories.map((category) => (
          <LegendItem key={category}>
            <LegendHeader>
              <LegendName
                $color={
                  priceEvaluationColors[category] || 'priceEvaluationNoneText'
                }
              >
                {t(`priceEvaluation.${category}`)}
              </LegendName>
              <PriceEvaluationBlocks
                evaluation={category === 'none' ? null : category}
              />
            </LegendHeader>
            <Paragraph>
              {t(`priceEvaluationDialog.descriptions.${category}`)}
            </Paragraph>
          </LegendItem>
        ))}
      </Legend>
      <Paragraph>
        <Trans
          i18nKey="priceEvaluationDialog.note"
          components={[mobileDeLink]}
        />
      </Paragraph>
      <CloseButton type="button" onClick={onClose}>
        {t('close')}
      </CloseButton>
    </StyledDialog>
  )
}

PriceEvaluationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** The car to load the price ranges for */
  carId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The rating of this car, which tells whether mobile.de could evaluate it */
  evaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
}

PriceEvaluationDialog.defaultProps = {
  carId: null,
  evaluation: null,
  className: null,
}

export default PriceEvaluationDialog
